import { useState, useEffect, useRef, useCallback } from 'react';
import { useRealtimeStore } from '../store/realtimeStore';
import { toast } from 'react-hot-toast';
import Cookies from 'js-cookie';
import { initSocket, closeSocket, getSocket, sendMessage } from '../lib/socket';

export const useRealtimeProject = (projectId, user, currentOrg, { onTaskChange } = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [presence, setPresence] = useState([]);
  const [typingUsers, setTypingUsers] = useState(new Map());
  const wsRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  
  const {
    tasks,
    setTasks,
    updateTask,
    moveTask,
    reorderTasks,
    conflictTasks,
    addConflict,
    resolveConflict
  } = useRealtimeStore();

  const disconnect = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    closeSocket();
    wsRef.current = null;
    setIsConnected(false);
    setPresence([]);
    setTypingUsers(new Map());
  }, []);

  const handleRealtimeEvent = useCallback((message) => {
    const { event_type, actor_id, actor_name } = message;

    switch (event_type) {
      case 'presence.initial':
        setPresence(message.users || []);
        break;

      case 'presence.join':
        if (message.user_id !== user?.id) {
          setPresence(prev => [...prev.filter(u => u.user_id !== message.user_id), {
            user_id: message.user_id,
            user_name: actor_name,
            project_id: message.project_id,
            joined_at: message.timestamp,
            last_seen: message.timestamp,
            cursor_position: message.cursor_position,
            active_task_id: message.active_task_id
          }]);
          toast.success(`${actor_name} joined the project`);
        }
        break;

      case 'presence.leave':
        setPresence(prev => prev.filter(u => u.user_id !== message.user_id));
        setTypingUsers(prev => {
          const newMap = new Map(prev);
          newMap.delete(message.user_id);
          return newMap;
        });
        if (message.user_id !== user?.id) {
          toast(`${actor_name} left the project`);
        }
        break;

      case 'presence.update':
        setPresence(prev => prev.map(u =>
          u.user_id === message.user_id
            ? { ...u, cursor_position: message.cursor_position, active_task_id: message.active_task_id, last_seen: message.timestamp }
            : u
        ));
        break;

      case 'task.create':
        if (actor_id !== user?.id) {
          updateTask(message.task_data);
          onTaskChange?.();
          toast.success(`${actor_name} created task: ${message.task_data.title}`);
        }
        break;

      case 'task.update':
        if (actor_id !== user?.id) {
          updateTask({
            id: message.task_id,
            ...message.changes,
            version: message.version,
            updated_at: message.timestamp
          });
          onTaskChange?.();
          toast(`${actor_name} updated a task`);
        }
        break;

      case 'task.move':
        if (actor_id !== user?.id) {
          moveTask(message.task_id, message.to_status, message.new_position);
          onTaskChange?.();
          toast(`${actor_name} moved a task to ${message.to_status.replace(/_/g, ' ')}`);
        }
        break;

      case 'task.reorder':
        if (actor_id !== user?.id) {
          reorderTasks(message.status, message.task_ids);
          onTaskChange?.();
        }
        break;

      case 'task.delete':
        if (actor_id !== user?.id) {
          setTasks(prev => prev.filter(t => t.id !== message.task_id));
          onTaskChange?.();
          toast.error(`${actor_name} deleted a task`);
        }
        break;

      case 'typing.start':
        if (message.user_id !== user?.id) {
          setTypingUsers(prev => {
            const newMap = new Map(prev);
            newMap.set(message.user_id, {
              user_name: actor_name,
              task_id: message.task_id,
              context: message.context,
              timestamp: message.timestamp
            });
            return newMap;
          });
        }
        break;

      case 'typing.stop':
        if (message.user_id !== user?.id) {
          setTypingUsers(prev => {
            const newMap = new Map(prev);
            newMap.delete(message.user_id);
            return newMap;
          });
        }
        break;

      case 'conflict.detected':
        addConflict(message.entity_id, {
          entity_type: message.entity_type,
          server_data: message.server_data,
          client_version: message.client_version,
          server_version: message.server_version
        });
        toast.error(`Conflict detected: ${message.entity_type} was modified by another user`);
        break;

      case 'comment.create':
        if (actor_id !== user?.id) {
          toast(`${actor_name} added a comment`);
        }
        break;

      case 'comment.update':
        if (actor_id !== user?.id) {
          toast(`${actor_name} updated a comment`);
        }
        break;

      case 'comment.delete':
        if (actor_id !== user?.id) {
          toast.error(`${actor_name} deleted a comment`);
        }
        break;

      default:
        console.log('Unhandled real-time event:', message);
    }
  }, [user, onTaskChange, updateTask, moveTask, reorderTasks, setTasks, addConflict]);

  const connect = useCallback(() => {
    if (!projectId || !user || !currentOrg) return;

    const token = Cookies.get('access_token');
    if (!token) return;

    try {
      const socket = initSocket(`/ws/projects/${projectId}`, token, {
        onOpen: () => {
          setIsConnected(true);
          heartbeatIntervalRef.current = setInterval(() => {
            const currentSocket = getSocket();
            if (currentSocket && currentSocket.getReadyState() === WebSocket.OPEN) {
              currentSocket.send(JSON.stringify({ event_type: 'heartbeat' }));
            }
          }, 30000);
        },
        onMessage: (event) => {
          try {
            const message = JSON.parse(event.data);
            handleRealtimeEvent(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        },
        onError: (error) => {
          console.error('[WS] Project connection error:', error);
          toast.error('Real-time connection error — retrying…');
        },
        onClose: (event) => {
          setIsConnected(false);
          setPresence([]);
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
          }
          if (event.code === 4001) {
            console.error('[WS] Project auth failed (4001) — token expired. Please refresh the page.');
            toast.error('Session expired — please refresh the page to reconnect.');
          } else if (event.code === 4003) {
            console.error('[WS] Project access denied (4003) — not a project member.');
            toast.error('You no longer have access to this project.');
          } else {
            console.log(`[WS] Project closed: code=${event.code} reason="${event.reason}"`);
          }
        }
      });

      wsRef.current = socket;
    } catch (error) {
      console.error('Failed to connect to project WebSocket:', error);
    }
  }, [projectId, user, currentOrg, handleRealtimeEvent]);

  // Send real-time events
  const sendPresenceUpdate = useCallback((cursorPosition, activeTaskId) => {
    const socket = getSocket();
    if (socket && socket.getReadyState() === WebSocket.OPEN) {
      sendMessage({
        event_type: 'presence.update',
        cursor_position: cursorPosition,
        active_task_id: activeTaskId
      });
    }
  }, []);

  const sendTypingStart = useCallback((taskId, context = 'task') => {
    const socket = getSocket();
    if (socket && socket.getReadyState() === WebSocket.OPEN) {
      sendMessage({
        event_type: 'typing.start',
        task_id: taskId,
        context
      });
    }
  }, []);

  const sendTypingStop = useCallback((taskId, context = 'task') => {
    const socket = getSocket();
    if (socket && socket.getReadyState() === WebSocket.OPEN) {
      sendMessage({
        event_type: 'typing.stop',
        task_id: taskId,
        context
      });
    }
  }, []);

  // Connect/disconnect on mount/unmount
  useEffect(() => {
    if (projectId) {
      connect();
    }
    return disconnect;
  }, [projectId, connect, disconnect]);

  // Cleanup on user/org change
  useEffect(() => {
    if (!user || !currentOrg) {
      disconnect();
    }
  }, [user, currentOrg, disconnect]);

  return {
    isConnected,
    presence,
    typingUsers: Array.from(typingUsers.values()),
    tasks,
    conflictTasks,
    sendPresenceUpdate,
    sendTypingStart,
    sendTypingStop,
    resolveConflict
  };
};